/* Shared by the theme's cart handlers. This is a storefront UX rule. */
(() => {
  if (window.ZrexCartLimit) return;
  const config = JSON.parse(document.getElementById('zrex-cart-limit-config').textContent);
  const limit = Number(config.limit) || 20;
  const root = window.Shopify?.routes?.root || '/';
  let tail = Promise.resolve();
  let pending = 0;
  let currentCount = Number(config.count) || 0;
  const message = config.message.replace('[limit]', limit);
  const overMessage = config.overMessage.replace('[limit]', limit);

  function syncUI() {
    document.querySelectorAll('[data-zrex-cart-count]').forEach((el) => { el.dataset.zrexCartCount = String(currentCount); });
    document.querySelectorAll('[data-zrex-cart-quantity]').forEach((input) => {
      input.readOnly = true;
      const group = input.closest('quantity-input');
      if (!group) return;
      const plus = group.querySelector('[name="plus"]');
      const minus = group.querySelector('[name="minus"]');
      if (plus) plus.disabled = Boolean(pending || input.disabled || currentCount >= limit || Number(input.value) >= Number(input.max));
      if (minus) minus.disabled = Boolean(pending || input.disabled || Number(input.value) <= 0);
      group.setAttribute('aria-busy', String(pending > 0));
    });
    document.querySelectorAll('[data-zrex-cart-limit-message]').forEach((el) => {
      el.textContent = currentCount > limit ? overMessage : message;
      el.hidden = currentCount < limit;
    });
    document.querySelectorAll('#CartDrawer-Checkout, #checkout').forEach((button) => {
      button.disabled = pending > 0 || currentCount > limit || currentCount === 0;
    });
  }

  async function getCart() {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
    if (!response.ok) throw new Error(config.retryMessage);
    const cart = await response.json();
    if (!Array.isArray(cart.items) || !Number.isInteger(cart.item_count)) throw new Error(config.retryMessage);
    currentCount = cart.item_count;
    return cart;
  }

  function sectionEntries() {
    const entries = [];
    document.querySelectorAll('cart-items, cart-drawer-items').forEach((element) => {
      for (const section of element.getSectionsToRender?.() || []) entries.push(section);
    });
    return entries;
  }

  function paintSections(sections, skip = new Set()) {
    const rendered = new Set(skip);
    for (const entry of sectionEntries()) {
      if (rendered.has(entry.id) || !sections?.[entry.section]) continue;
      const mount = document.getElementById(entry.id);
      if (!mount) continue;
      const target = mount.querySelector(entry.selector) || mount;
      const source = new DOMParser().parseFromString(sections[entry.section], 'text/html').querySelector(entry.selector);
      if (source) { target.replaceChildren(...source.childNodes); rendered.add(entry.id); }
    }
  }

  async function refresh(cart) {
    cart = cart || await getCart();
    const entries = sectionEntries();
    const ids = [...new Set(entries.map((entry) => entry.section))];
    if (ids.length) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', ids.slice(0, 5).join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!response.ok) throw new Error(config.retryMessage);
      const sections = await response.json();
      paintSections(sections);
    }
    document.querySelectorAll('cart-drawer, cart-items, #main-cart-footer').forEach((element) => element.classList.toggle('is-empty', cart.item_count === 0));
    currentCount = cart.item_count;
    syncUI();
    return cart;
  }

  function showError(context, text) {
    const container = context?.closest?.('cart-drawer-items, cart-items, product-form') || context;
    const error = container?.querySelector?.('#CartDrawer-CartErrors, .product-form__error-message, .zrex-cart-request-error') ||
      document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (error) {
      error.textContent = text;
      error.closest('.product-form__error-message-wrapper')?.removeAttribute('hidden');
    }
  }

  function payloadFor(options) {
    if (options.body instanceof FormData) {
      return { id: options.body.get('id'), quantity: Number(options.body.get('quantity') || 1) };
    }
    return typeof options.body === 'string' ? JSON.parse(options.body) : {};
  }

  function projectedCount(cart, url, payload) {
    if (/\/add(?:\.js)?$/.test(url.pathname)) {
      return cart.item_count + (payload.items || [payload]).reduce((sum, item) => sum + Number(item.quantity || 1), 0);
    }
    if (/\/change(?:\.js)?$/.test(url.pathname)) {
      const line = payload.id ? cart.items.find((item) => item.key === String(payload.id) || String(item.variant_id) === String(payload.id)) : cart.items[Number(payload.line) - 1];
      if (!line) throw new Error(config.retryMessage);
      return cart.item_count - line.quantity + Number(payload.quantity);
    }
    if (payload.updates) {
      if (Array.isArray(payload.updates)) return payload.updates.reduce((sum, qty) => sum + Number(qty), 0);
      return Object.entries(payload.updates).reduce((count, [id, qty]) => {
        const line = cart.items.find((item) => item.key === id || String(item.variant_id) === id);
        return count - (line?.quantity || 0) + Number(qty);
      }, cart.item_count);
    }
    return cart.item_count;
  }

  function errorResponse(text, code = 'ZREX_CART_RETRY') {
    return new Response(JSON.stringify({ status: 422, code, errors: text, description: text, message: text }), { status: 422, headers: { 'Content-Type': 'application/json' } });
  }

  function request(url, options, context) {
    pending += 1;
    syncUI();
    const task = tail.then(async () => {
      try {
        const before = await getCart();
        const projected = projectedCount(before, new URL(url, window.location.origin), payloadFor(options));
        if (projected > limit && projected > before.item_count) {
          await refresh(before);
          showError(context, message);
          return errorResponse(message, 'ZREX_CART_LIMIT');
        }
        const sections = [...new Set(sectionEntries().map((entry) => entry.section))].slice(0, 5);
        if (options.body instanceof FormData) {
          options.body.set('sections', sections.join(','));
        } else if (typeof options.body === 'string') {
          const body = JSON.parse(options.body);
          body.sections = [...new Set([...(body.sections || []), ...sections])].slice(0, 5);
          options = { ...options, body: JSON.stringify(body) };
        }
        const response = await fetch(url, options);
        const result = await response.clone().json();
        if (!response.ok || result.errors || result.status) {
          await refresh();
        } else if (Number.isInteger(result.item_count)) {
          currentCount = result.item_count;
        } else {
          // Adds return only added lines; read the server-rendered drawer's count.
          const section = Object.values(result.sections || {}).find((html) => typeof html === 'string' && html.includes('data-zrex-cart-count='));
          const marker = section && new DOMParser().parseFromString(section, 'text/html').querySelector('[data-zrex-cart-count]');
          if (marker) currentCount = Number(marker.dataset.zrexCartCount);
          else await getCart();
        }
        if (response.ok && !result.errors && !result.status) {
          const ownSections = context?.matches?.('cart-items, cart-drawer-items')
            ? context.getSectionsToRender().map((entry) => entry.id)
            : ['CartDrawer', 'cart-icon-bubble'];
          paintSections(result.sections, new Set(ownSections));
        }
        return response;
      } catch (error) {
        const text = error.message || config.retryMessage;
        showError(context, text);
        return errorResponse(text);
      } finally {
        pending -= 1;
        syncUI();
        setTimeout(syncUI, 0);
      }
    });
    tail = task.catch(() => {});
    return task;
  }

  // Checkout uses Shopify's saved cart, without re-submitting stale quantity fields.
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!['cart', 'CartDrawer-Form'].includes(form.id) || event.submitter?.name !== 'checkout') return;
    event.preventDefault();
    if (pending) return;
    pending += 1;
    syncUI();
    try {
      const cart = await getCart();
      if (cart.item_count > limit) { await refresh(cart); showError(form, overMessage); return; }
      if (cart.item_count) window.location.assign(`${root}checkout`);
    } catch (error) { showError(form, config.retryMessage); }
    finally { pending -= 1; syncUI(); }
  }, true);

  window.ZrexCartLimit = { request, refresh, getCart, syncUI, limit, get busy() { return pending > 0; } };
  document.addEventListener('shopify:section:load', syncUI);
  document.addEventListener('DOMContentLoaded', syncUI, { once: true });
})();
